import { useQuery } from '@tanstack/react-query';
import { featureApi, MOCK_MODE } from '../lib';
import { useCurrentProject } from './use-current-project';

export interface DashboardStats {
  totalUsers: number;
  activeQuests: number;
  memesGenerated: number;
  pointsAwarded: number;
}

const MOCK_STATS: DashboardStats = {
  totalUsers: 1234,
  activeQuests: 8,
  memesGenerated: 5678,
  pointsAwarded: 125000,
};

export const dashboardStatsKeys = {
  all: ['dashboardStats'] as const,
  byProject: (projectId: string) => [...dashboardStatsKeys.all, projectId] as const,
};

export function useDashboardStats(): ReturnType<typeof useQuery<DashboardStats>> {
  const { projectId } = useCurrentProject();

  return useQuery({
    queryKey: dashboardStatsKeys.byProject(projectId ?? ''),
    queryFn: async () => {
      if (MOCK_MODE) return MOCK_STATS;

      // Aggregate from multiple endpoints
      const [quests, leaderboard] = await Promise.allSettled([
        featureApi.get<{ total: number; active: number }>(`/quests/stats?projectId=${projectId}`),
        featureApi.get<{ totalParticipants: number; totalPoints: number }>(`/points/leaderboard/stats?projectId=${projectId}`),
      ]);

      return {
        totalUsers: leaderboard.status === 'fulfilled' ? leaderboard.value.totalParticipants : 0,
        activeQuests: quests.status === 'fulfilled' ? quests.value.active : 0,
        memesGenerated: 0,
        pointsAwarded: leaderboard.status === 'fulfilled' ? leaderboard.value.totalPoints : 0,
      };
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5,
  });
}
